import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Eye, X, ArrowLeft, AlertTriangle, EyeOff, Loader2 } from 'lucide-react';
import type { MonitorAlert, PublicRoom } from '../../shared/protocol';
import { realtime } from '../lib/realtime';
import { Avatar } from './Avatar';
import { AlertChips, alertTotal } from './AlertChips';

interface Props {
  room: PublicRoom;
}

/**
 * Panel del anfitrión en salas supervisadas: avisos de posible trampa por
 * jugador y, al elegir uno, su pantalla en vivo. Lo que se ve es lo que
 * ocurre dentro de la pestaña de Open Dahu del jugador, nada más.
 */
export const MonitorPanel: React.FC<Props> = ({ room }) => {
  const [open, setOpen] = useState(false);
  const [watching, setWatching] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<Record<string, MonitorAlert>>({});

  useEffect(
    () =>
      realtime.on((msg) => {
        if (msg.t === 'rec:alerts') setAlerts(msg.alerts);
      }),
    [],
  );

  // Al abrir el panel se piden los avisos acumulados.
  useEffect(() => {
    if (open) realtime.send({ t: 'host:watch', playerId: watching });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const watch = (playerId: string | null) => {
    setWatching(playerId);
    realtime.send({ t: 'host:watch', playerId });
  };

  const close = () => {
    setOpen(false);
    setWatching(null);
    realtime.send({ t: 'host:watch', playerId: null });
  };

  const players = useMemo(
    () =>
      room.players
        .filter((p) => p.id !== room.hostId)
        .sort(
          (a, b) =>
            alertTotal(alerts[b.id]) - alertTotal(alerts[a.id]) ||
            a.displayName.localeCompare(b.displayName),
        ),
    [room.players, room.hostId, alerts],
  );

  const total = Object.values(alerts).reduce((n, a) => n + alertTotal(a), 0);
  const watched = players.find((p) => p.id === watching);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-40 flex items-center gap-2 px-4 py-3 rounded-full font-semibold
                   bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-100 shadow-lg transition-colors"
        aria-label="Abrir supervisión de jugadores"
      >
        <Eye className="w-5 h-5 text-sky-400" />
        Supervisión
        {total > 0 && (
          <span className="min-w-6 px-1.5 h-6 flex items-center justify-center rounded-full bg-ember-500 text-on-accent text-xs font-bold">
            {total}
          </span>
        )}
      </button>

      {open && (
        <aside
          className="fixed inset-y-0 right-0 z-50 w-full sm:w-[30rem] bg-ink-900 border-l border-ink-700 shadow-2xl flex flex-col"
          aria-label="Supervisión de jugadores"
        >
          <div className="flex items-center gap-2 p-4 border-b border-ink-800">
            {watched && (
              <button
                onClick={() => watch(null)}
                className="p-2 rounded-lg text-ink-400 hover:text-ink-100 hover:bg-ink-800"
                aria-label="Volver a la lista"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <h2 className="flex-1 font-bold truncate">
              {watched ? watched.displayName : 'Supervisión'}
            </h2>
            <button
              onClick={close}
              className="p-2 rounded-lg text-ink-400 hover:text-ink-100 hover:bg-ink-800"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {watched ? (
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <LiveScreen playerId={watched.id} />
              <AlertChips alert={alerts[watched.id]} showZero />
              {!watched.connected && (
                <p className="text-sm text-ink-500 flex items-center gap-2">
                  <EyeOff className="w-4 h-4" /> Este jugador está desconectado.
                </p>
              )}
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              <p className="text-xs text-ink-500 px-1 pb-1">
                Ves lo que ocurre dentro de Open Dahu. No se ve otra pestaña, otra app ni el
                celular. La partida se guarda para revisarla después en Grabaciones.
              </p>
              {players.length === 0 && (
                <p className="text-ink-400 text-sm p-4 text-center">Aún no hay jugadores.</p>
              )}
              {players.map((p) => {
                const a = alerts[p.id];
                return (
                  <button
                    key={p.id}
                    onClick={() => watch(p.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors
                                ${alertTotal(a) > 0 ? 'border-ember-600/60 bg-ember-500/5' : 'border-ink-700 bg-ink-850'}
                                hover:border-sky-600 ${p.connected ? '' : 'opacity-50'}`}
                  >
                    <Avatar config={p.avatar} size={36} />
                    <span className="flex-1 min-w-0">
                      <span className="block font-medium text-ink-100 truncate">{p.displayName}</span>
                      <AlertChips alert={a} />
                    </span>
                    {alertTotal(a) > 0 && <AlertTriangle className="w-4 h-4 text-ember-400 shrink-0" />}
                    <Eye className="w-4 h-4 text-sky-400 shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </aside>
      )}
    </>
  );
};

/* ------------------------------------------------------------------ *
 * Pantalla en vivo
 * ------------------------------------------------------------------ */

const LiveScreen: React.FC<{ playerId: string }> = ({ playerId }) => {
  const holder = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'loading' | 'ready'>('loading');

  useEffect(() => {
    const el = holder.current;
    if (!el) return;
    let cancelled = false;
    let replayer: import('rrweb').Replayer | null = null;
    const fit = (width: number, height: number) => {
      if (!replayer || !el) return;
      const scale = Math.min(1, el.clientWidth / width);
      replayer.wrapper.style.transform = `scale(${scale})`;
      replayer.wrapper.style.transformOrigin = 'top left';
      el.style.height = `${Math.round(height * scale)}px`;
    };

    /**
     * Los relojes del jugador y del anfitrión no coinciden y la pantalla del
     * jugador puede llevar segundos sin emitir nada, así que no se confía en
     * las marcas de tiempo originales: cada lote se reubica de modo que su
     * último evento coincida con la hora a la que llegó, conservando los
     * intervalos entre sus eventos.
     */
    const rebase = (events: unknown[], anchor: number) => {
      const list = events as { timestamp: number }[];
      const last = list[list.length - 1]?.timestamp ?? anchor;
      return list.map((e) => ({ ...e, timestamp: anchor - (last - e.timestamp) }));
    };

    const start = async (events: unknown[]) => {
      const { Replayer } = await import('rrweb');
      await import('rrweb/dist/style.css');
      if (cancelled) return;
      el.innerHTML = '';
      // Sin DOM virtual: con él, el historial se aplica en un árbol que no se
      // vuelca a la pantalla hasta el siguiente evento y el visor se queda en
      // la primera imagen.
      replayer = new Replayer([], {
        root: el,
        liveMode: true,
        useVirtualDom: false,
        mouseTail: { strokeStyle: '#38bdf8' },
      });
      replayer.on('resize', (dim) => {
        const d = dim as { width: number; height: number };
        fit(d.width, d.height);
      });
      const anchor = Date.now();
      replayer.startLive(anchor);
      for (const e of rebase(events, anchor)) replayer.addEvent(e as never);
      setState('ready');
    };

    // Los lotes se encadenan: mientras rrweb carga o se reconstruye la pantalla,
    // los siguientes esperan en lugar de arrancar otro visor encima.
    let chain: Promise<void> = Promise.resolve();
    let hasSnapshot = false;

    const off = realtime.on((msg) => {
      if (msg.t !== 'rec:live' || msg.playerId !== playerId) return;
      const events = msg.events;
      const reset = msg.reset === true;
      chain = chain.then(async () => {
        if (cancelled) return;
        if (reset) {
          replayer?.destroy();
          replayer = null;
          hasSnapshot = false;
          setState('loading');
        }
        if (!replayer) {
          // Sin una foto completa de la pantalla no hay nada que reproducir.
          if (!hasSnapshot && !events.some((e) => (e as { type: number }).type === 2)) return;
          hasSnapshot = true;
          await start(events);
          return;
        }
        for (const e of rebase(events, Date.now())) replayer.addEvent(e as never);
      });
    });

    return () => {
      cancelled = true;
      off();
      replayer?.destroy();
    };
  }, [playerId]);

  return (
    <div className="space-y-2">
      <div
        ref={holder}
        className="w-full min-h-40 rounded-xl overflow-hidden border border-ink-700 bg-ink-950 relative"
      />
      {state === 'loading' && (
        <p className="text-sm text-ink-400 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Esperando la pantalla del jugador…
        </p>
      )}
    </div>
  );
};
